import React, { useState, useEffect, useCallback } from 'react';
import { S3Client } from '@aws-sdk/client-s3';
import toast from 'react-hot-toast';

import FileManagerSection from '../components/FileManagerSection';
import UploadHistoryModal from '../components/UploadHistoryModal';
import DownloadHistoryModal from '../components/DownloadHistoryModal';
import { apiCall } from '../services/api';
import DashboardSwitcher from '../components/DashboardSwitcher';
import MFANotificationBell from '../components/MFANotificationBell';

interface Bucket {
  bucketName: string;
  permissions: {
    list: boolean;
    download: boolean;
    upload: boolean;
    delete: boolean;
    maxObjectSize: number;
    monthlyDataTransferCap: number;
  };
  folderPath?: string | null; // Folder restriction path
  folderRestrictions?: Array<{
    folderPath: string;
    permissions: any;
  }>;
}

// Generate a unique key for bucket entries (handles multiple folder permissions for same bucket)
function getBucketKey(bucket: Bucket): string {
  return bucket.folderPath ? `${bucket.bucketName}#${bucket.folderPath}` : bucket.bucketName;
}

interface UsageStats {
  monthlyDataUsage: number;
  monthlyDownloadSize: number;
  monthlyUploadSize: number;
  remainingTransfer: number;
  bucketStats?: Record<string, {
    permissionKey: string;
    monthlyDataUsage: number;
    monthlyDownloadSize: number;
    monthlyUploadSize: number;
    monthlyDataTransferCap: number;
  }>;
}

interface UserDashboardProps {
  onSwitchToManagement?: () => void;
  dashboardMode?: 'management' | 'browser';
  userRole?: 'user' | 'manager' | 'admin';
}

const UserDashboard: React.FC<UserDashboardProps> = ({
  onSwitchToManagement,
  dashboardMode = 'browser',
  userRole = 'user'
}) => {
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [selectedBucketKey, setSelectedBucketKey] = useState<string>('');
  const [s3Client, setS3Client] = useState<S3Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [usageStats, setUsageStats] = useState<UsageStats>({ 
    monthlyDataUsage: 0, 
    monthlyDownloadSize: 0, 
    monthlyUploadSize: 0, 
    remainingTransfer: 0,
    bucketStats: {}
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [uploadHistoryOpen, setUploadHistoryOpen] = useState(false);
  const [downloadHistoryOpen, setDownloadHistoryOpen] = useState(false);

  // Folder size state: { [bucketKey]: { loading, size, objectCount, isApproximate, cached } }
  const [folderSizes, setFolderSizes] = useState<Record<string, {
    loading: boolean;
    totalSize?: number;
    objectCount?: number;
    isApproximate?: boolean;
    cached?: boolean;
    error?: string;
  }>>({});

  // Get the currently selected bucket object
  const selectedBucket = buckets.find(b => getBucketKey(b) === selectedBucketKey);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // Get user buckets
      const bucketsResponse = await apiCall('/s3/buckets');
      setBuckets(bucketsResponse);

      // Get temporary credentials (just for validation)
      const credentialsResponse = await apiCall('/s3/credentials');

      // Create a dummy S3Client for compatibility (not actually used)
      const client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION!,
        credentials: {
          accessKeyId: 'dummy',
          secretAccessKey: 'dummy',
          sessionToken: 'dummy'
        }
      });

      setS3Client(client);

      if (bucketsResponse.length > 0) {
        setSelectedBucketKey(getBucketKey(bucketsResponse[0]));
      }

      // Fetch actual user stats from backend (now includes per-bucket stats)
      try {
        const statsResponse = await apiCall('/s3/my-stats');
        const monthlyUsageBytes = statsResponse.monthlyDataUsage || 0;
        const monthlyDownloadBytes = statsResponse.monthlyDownloadSize || 0;
        const monthlyUploadBytes = statsResponse.monthlyUploadSize || 0;
        
        // Store raw stats including per-bucket stats
        setUsageStats({
          monthlyDataUsage: monthlyUsageBytes,
          monthlyDownloadSize: monthlyDownloadBytes,
          monthlyUploadSize: monthlyUploadBytes,
          remainingTransfer: Infinity, // Will be recalculated when bucket is selected
          bucketStats: statsResponse.bucketStats || {}
        });
      } catch (statsError) {
        console.error('Error loading user stats:', statsError);
        // Set default values if stats fetch fails
        setUsageStats({
          monthlyDataUsage: 0,
          monthlyDownloadSize: 0,
          monthlyUploadSize: 0,
          remainingTransfer: Infinity,
          bucketStats: {}
        });
      }

      toast.success('Dashboard loaded successfully');
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = () => {
    // Trigger refresh of file browser and reload stats
    setRefreshTrigger(prev => prev + 1);
    // Reload stats to get updated usage
    loadUserData();
  };

  // Format bytes helper
  const formatSizeDisplay = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate folder size for a bucket (lazy load on button click)
  const calculateFolderSize = useCallback(async (bucket: Bucket) => {
    const key = getBucketKey(bucket);
    setFolderSizes(prev => ({ ...prev, [key]: { loading: true } }));
    try {
      const prefix = bucket.folderPath || '';
      const res = await apiCall(`/s3/folder-size/${encodeURIComponent(bucket.bucketName)}/${encodeURIComponent(prefix)}`);
      setFolderSizes(prev => ({
        ...prev,
        [key]: {
          loading: false,
          totalSize: res.totalSize,
          objectCount: res.objectCount,
          isApproximate: res.isApproximate,
          cached: res.cached
        }
      }));
    } catch (err: any) {
      console.error('Failed to calculate folder size:', err);
      setFolderSizes(prev => ({
        ...prev,
        [key]: { loading: false, error: err.message || 'Failed to calculate' }
      }));
    }
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 space-y-6">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-primary-400 rounded-full animate-spin" style={{ animationDelay: '150ms' }}></div>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Dashboard</h3>
          <p className="text-gray-600">Fetching your S3 buckets and permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Top Bar with Dashboard Switcher and MFA Notification */}
      <div className="flex justify-between items-center">
        {/* Dashboard Switcher for Admin/Manager */}
        {onSwitchToManagement && userRole && (userRole === 'admin' || userRole === 'manager') ? (
          <DashboardSwitcher
            currentMode={dashboardMode}
            userRole={userRole}
            onSwitchToManagement={onSwitchToManagement}
          />
        ) : (
          <div></div>
        )}

        {/* MFA Notification Bell */}
        <MFANotificationBell />
      </div>

      {/* Modern Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
          </svg>
        </div>
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">My S3 Buckets</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Securely manage your files and folders with enterprise-grade cloud storage
          </p>
        </div>
      </div>

      {/* Stats Cards - 5 cards in single row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Buckets</p>
              <p className="text-xl font-bold text-gray-900">{buckets.length}</p>
            </div>
          </div>
        </div>

        {/* Permissions Card - Combined Upload/Delete */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Permissions</p>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center text-sm">
                  <svg className="w-3 h-3 text-purple-500 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 11-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold text-gray-900">{buckets.filter(b => b.permissions.upload).length}</span>
                </span>
                <span className="text-gray-300">|</span>
                <span className="inline-flex items-center text-sm">
                  <svg className="w-3 h-3 text-red-500 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold text-gray-900">{buckets.filter(b => b.permissions.delete).length}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Downloads Card - Clickable for Download History */}
        <div
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all"
          onClick={() => setDownloadHistoryOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDownloadHistoryOpen(true); }}
          aria-label="View download history"
          title="Click to view download history"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Downloads</p>
              <p className="text-xl font-bold text-gray-900">
                {usageStats.monthlyDownloadSize >= 1024 * 1024 * 1024 
                  ? `${(usageStats.monthlyDownloadSize / (1024 * 1024 * 1024)).toFixed(1)}GB`
                  : `${(usageStats.monthlyDownloadSize / (1024 * 1024)).toFixed(1)}MB`}
              </p>
            </div>
          </div>
        </div>

        {/* Uploads Card - Clickable for Upload History */}
        <div
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all"
          onClick={() => setUploadHistoryOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setUploadHistoryOpen(true); }}
          aria-label="View upload history"
          title="Click to view upload history"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 11-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Uploads</p>
              <p className="text-xl font-bold text-gray-900">
                {usageStats.monthlyUploadSize >= 1024 * 1024 * 1024 
                  ? `${(usageStats.monthlyUploadSize / (1024 * 1024 * 1024)).toFixed(1)}GB`
                  : `${(usageStats.monthlyUploadSize / (1024 * 1024)).toFixed(1)}MB`}
              </p>
            </div>
          </div>
        </div>

        {/* Remaining Card */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">
                Remaining {selectedBucket ? `(${selectedBucket.bucketName.split('#')[0]})` : ''}
              </p>
              <p className="text-xl font-bold text-gray-900">
                {(() => {
                  if (!selectedBucket || selectedBucket.permissions.monthlyDataTransferCap <= 0) {
                    return '∞';
                  }
                  // Use per-bucket stats for accurate remaining calculation
                  const bucketKey = getBucketKey(selectedBucket);
                  const bucketUsage = usageStats.bucketStats?.[bucketKey]?.monthlyDataUsage || 0;
                  const capInBytes = selectedBucket.permissions.monthlyDataTransferCap * 1024 * 1024 * 1024;
                  const remainingBytes = Math.max(0, capInBytes - bucketUsage);
                  const remainingGB = remainingBytes / (1024 * 1024 * 1024);
                  return remainingGB >= 1 
                    ? `${remainingGB.toFixed(1)}GB`
                    : `${(remainingGB * 1024).toFixed(0)}MB`;
                })()}
              </p>
              {selectedBucket && selectedBucket.permissions.monthlyDataTransferCap > 0 && (
                <p className="text-xs text-gray-400">
                  of {selectedBucket.permissions.monthlyDataTransferCap}GB cap
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Buckets Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">Available Buckets</h2>
          </div>
        </div>

        <div className="p-6">
          {buckets.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No buckets assigned</h3>
              <p className="text-gray-500 mb-6">Contact your administrator to get access to S3 buckets.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {buckets.map((bucket) => {
                const bucketKey = getBucketKey(bucket);
                const isSelected = selectedBucketKey === bucketKey;
                const cardClasses = isSelected
                  ? 'border-primary-500 bg-primary-50 shadow-lg ring-2 ring-primary-200'
                  : 'border-gray-200 bg-white hover:border-primary-300';

                return (
                  <div
                    key={bucketKey}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1 ${cardClasses}`}
                    onClick={() => setSelectedBucketKey(bucketKey)}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2">
                        <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Folder restriction indicator */}
                    {bucket.folderPath && (
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          Folder Access
                        </span>
                      </div>
                    )}

                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${isSelected ? 'bg-primary-600' : 'bg-gray-100'
                        }`}>
                        <svg className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate text-lg mb-1">
                          {bucket.bucketName}
                        </h3>
                        
                        {/* Show folder path if restricted */}
                        {bucket.folderPath && (
                          <p className="text-sm text-blue-600 mb-2 truncate" title={bucket.folderPath}>
                            📁 {bucket.folderPath}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 mb-3">
                          {bucket.permissions.list && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                              List
                            </span>
                          )}
                          {bucket.permissions.download && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Download
                            </span>
                          )}
                          {bucket.permissions.upload && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 11-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                              Upload
                            </span>
                          )}
                          {bucket.permissions.delete && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              Delete
                            </span>
                          )}
                        </div>

                        {bucket.permissions.maxObjectSize > 0 && (
                          <p className="text-sm text-gray-500">
                            Max file size: <span className="font-medium">{bucket.permissions.maxObjectSize} MB</span>
                          </p>
                        )}

                        {/* Folder Size - Lazy Load */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          {(() => {
                            const sizeData = folderSizes[bucketKey];
                            if (!sizeData) {
                              return (
                                <button
                                  onClick={(e) => { e.stopPropagation(); calculateFolderSize(bucket); }}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                  aria-label={`Calculate folder size for ${bucket.bucketName}`}
                                >
                                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                  Calculate Size
                                </button>
                              );
                            }
                            if (sizeData.loading) {
                              return (
                                <div className="flex items-center text-xs text-gray-500">
                                  <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin mr-2"></div>
                                  Calculating...
                                </div>
                              );
                            }
                            if (sizeData.error) {
                              return (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-red-500">{sizeData.error}</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); calculateFolderSize(bucket); }}
                                    className="text-xs text-indigo-600 hover:underline ml-2"
                                  >
                                    Retry
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center text-sm">
                                  <span className="text-gray-500 mr-1">📊</span>
                                  <span className="font-medium text-gray-900">{formatSizeDisplay(sizeData.totalSize || 0)}</span>
                                  <span className="text-gray-400 ml-1.5 text-xs">
                                    ({(sizeData.objectCount || 0).toLocaleString()} file{sizeData.objectCount !== 1 ? 's' : ''})
                                  </span>
                                  {sizeData.isApproximate && (
                                    <span className="ml-1 text-xs text-amber-500" title="Approximate: object cap reached">≈</span>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); calculateFolderSize(bucket); }}
                                  className="text-gray-400 hover:text-indigo-600 transition-colors"
                                  title="Recalculate"
                                  aria-label="Recalculate folder size"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* File Management Section */}
      {selectedBucket && s3Client && (
        <FileManagerSection
          key={selectedBucketKey}
          selectedBucket={selectedBucket.bucketName}
          s3Client={s3Client}
          permissions={selectedBucket.permissions}
          refreshTrigger={refreshTrigger}
          onUploadComplete={handleUploadComplete}
          initialFolderPath={selectedBucket.folderPath || undefined}
        />
      )}

      {/* Upload History Modal */}
      <UploadHistoryModal
        isOpen={uploadHistoryOpen}
        onClose={() => setUploadHistoryOpen(false)}
      />

      {/* Download History Modal */}
      <DownloadHistoryModal
        isOpen={downloadHistoryOpen}
        onClose={() => setDownloadHistoryOpen(false)}
      />
    </div>
  );
};

export default UserDashboard;